import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { canEdit, canDelete, canEditModule } from "@/utils/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Package,
  Plus,
  Edit,
  Trash2,
  Search,
  AlertTriangle,
  FileSpreadsheet,
  FileText,
  ClipboardList,
  Coffee,
  Sparkles,
  Car,
  Wrench,
} from "lucide-react";

const CLASSIFICATIONS = [
  { value: "PAP", label: "Papelería y Artículos de Oficina", icon: FileSpreadsheet, color: "bg-blue-500" },
  { value: "AYB", label: "Alimentos y Bebidas", icon: Coffee, color: "bg-orange-500" },
  { value: "LIM", label: "Limpieza", icon: Sparkles, color: "bg-green-500" },
  { value: "INV", label: "Insumos Vehículos", icon: Car, color: "bg-purple-500" },
  { value: "MAN", label: "Mantenimiento", icon: Wrench, color: "bg-slate-500" },
];

const UNITS = ["piezas", "litros", "kg", "cajas", "paquetes", "rollos", "bolsas"];

const Logistics = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [articulosCatalogo, setArticulosCatalogo] = useState([]);
  const [articulosCostosMap, setArticulosCostosMap] = useState({});
  const [unidadesCatalogo, setUnidadesCatalogo] = useState(UNITS);
  const [areasResponsables, setAreasResponsables] = useState([]);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClassification, setFilterClassification] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    clasificacion: "PAP",
    articulo: "",
    descripcion: "",
    marca: "",
    presentacion: "",
    unidad: "piezas",
    stock_actual: 0,
    stock_minimo: 0,
    proveedor: "",
    area_responsable: "",
    ubicacion: "",
    costo_inicial: 0,
  });

  useEffect(() => {
    fetchItems();
    fetchProveedores();
    fetchSettings();
  }, [filterClassification]);

  const fetchSettings = async () => {
    try {
      const [settingsRes, articulosRes] = await Promise.all([
        axios.get(`${API}/settings`, { withCredentials: true }),
        axios.get(`${API}/articulos-catalogo/costos`, { withCredentials: true }),
      ]);
      if (settingsRes.data.unidades?.length) setUnidadesCatalogo(settingsRes.data.unidades);
      if (settingsRes.data.areas_responsables?.length) setAreasResponsables(settingsRes.data.areas_responsables);
      if (settingsRes.data.ubicaciones_logistica?.length) setUbicaciones(settingsRes.data.ubicaciones_logistica);
      if (articulosRes.data?.length) {
        setArticulosCatalogo(articulosRes.data.map(a => a.nombre));
        const costMap = {};
        articulosRes.data.forEach(a => { costMap[a.nombre] = a.costo_promedio || 0; });
        setArticulosCostosMap(costMap);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchProveedores = async () => {
    try {
      const res = await axios.get(`${API}/proveedores-gasto`, { withCredentials: true });
      setProveedores(res.data);
    } catch (error) {
      console.error("Error fetching proveedores:", error);
    }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const params = filterClassification !== "all" ? `?clasificacion=${filterClassification}` : "";
      const res = await axios.get(`${API}/inventory${params}`, { withCredentials: true });
      setItems(res.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error("Error al cargar inventario");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/inventory/${editingItem.id}`, formData, { withCredentials: true });
        toast.success("Artículo actualizado");
      } else {
        await axios.post(`${API}/inventory`, formData, { withCredentials: true });
        toast.success("Artículo creado");
      }
      setShowDialog(false);
      setEditingItem(null);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      clasificacion: item.clasificacion,
      articulo: item.articulo || "",
      descripcion: item.descripcion,
      marca: item.marca || "",
      presentacion: item.presentacion || "",
      unidad: item.unidad || "piezas",
      stock_actual: item.stock_actual ?? "",
      stock_minimo: item.stock_minimo ?? "",
      proveedor: item.proveedor || "",
      area_responsable: item.area_responsable || "",
      ubicacion: item.ubicacion || "",
      costo_inicial: item.costo_inicial ?? "",
    });
    setShowDialog(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("¿Eliminar este artículo?")) return;
    try {
      await axios.delete(`${API}/inventory/${itemId}`, { withCredentials: true });
      toast.success("Artículo eliminado");
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const resetForm = () => {
    setFormData({
      clasificacion: "PAP",
      articulo: "",
      descripcion: "",
      marca: "",
      presentacion: "",
      unidad: "piezas",
      stock_actual: 0,
      stock_minimo: 0,
      proveedor: "",
      area_responsable: "",
      ubicacion: "",
      costo_inicial: 0,
    });
  };

  const openNewDialog = () => {
    setEditingItem(null);
    resetForm();
    setShowDialog(true);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = 
      item.clave?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.articulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.marca?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getClassificationInfo = (code) => CLASSIFICATIONS.find(c => c.value === code) || CLASSIFICATIONS[0];

  const downloadHojaConteo = async () => {
    try {
      const params = new URLSearchParams();
      if (filterClassification && filterClassification !== "all") {
        params.append("clasificacion", filterClassification);
      }
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/productos/hoja-conteo/pdf${qs}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : "hoja_conteo_inventario.pdf";
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Hoja de conteo descargada");
    } catch (err) {
      // Try to read the JSON error message even though responseType is blob
      let detail = "";
      try {
        if (err.response?.data instanceof Blob) {
          const txt = await err.response.data.text();
          try { detail = JSON.parse(txt).detail || ""; } catch { detail = txt; }
        } else {
          detail = err.response?.data?.detail || "";
        }
      } catch { /* ignore */ }
      const status = err.response?.status;
      if (status === 404) {
        toast.error("La función aún no está disponible. Publica el redeploy y vuelve a intentar.");
      } else if (status === 401 || status === 403) {
        toast.error("Tu sesión expiró o no tienes permisos.");
      } else if (detail) {
        toast.error(`Error: ${detail}`);
      } else {
        toast.error("Error al generar la hoja de conteo");
      }
    }
  };

  const needsRestock = (item) => (Number(item.stock_actual) || 0) <= (Number(item.stock_minimo) || 0);

  // Stats
  const totalItems = items.length;
  const lowStockItems = items.filter(needsRestock).length;
  const statsByClass = CLASSIFICATIONS.map(c => ({
    ...c,
    count: items.filter(i => i.clasificacion === c.value).length
  }));

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="logistics-page">
      <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Inventario de Consumibles</h1>
          <p className="text-slate-500">Gestión de stock y reabastecimiento</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            onClick={downloadHojaConteo}
            data-testid="download-hoja-conteo-btn"
          >
            <ClipboardList className="w-4 h-4 mr-2" /> Descargar hoja de conteo
          </Button>
          <Button onClick={openNewDialog} className="bg-emerald-600 hover:bg-emerald-700" disabled={!canEditModule(user, "logistica_productos")}>
            <Plus className="w-4 h-4 mr-2" /> Nuevo Producto
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Package className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalItems}</p>
                <p className="text-xs text-slate-500">Total Artículos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={lowStockItems > 0 ? "border-red-200 bg-red-50" : ""}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${lowStockItems > 0 ? "bg-red-100" : "bg-slate-100"}`}>
                <AlertTriangle className={`w-5 h-5 ${lowStockItems > 0 ? "text-red-600" : "text-slate-600"}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{lowStockItems}</p>
                <p className="text-xs text-slate-500">Stock Bajo</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {statsByClass.map(stat => (
          <Card key={stat.value}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${stat.color} bg-opacity-20 rounded-lg`}>
                  <stat.icon className={`w-5 h-5 ${stat.color.replace("bg-", "text-")}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stat.count}</p>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por clave, descripción o marca..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterClassification} onValueChange={setFilterClassification}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtrar por clasificación" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las clasificaciones</SelectItem>
                {CLASSIFICATIONS.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>CLAVE</TableHead>
                <TableHead>CLASIFICACIÓN</TableHead>
                <TableHead>ARTÍCULO</TableHead>
                <TableHead>DESCRIPCIÓN</TableHead>
                <TableHead>MARCA</TableHead>
                <TableHead>PRESENTACIÓN</TableHead>
                <TableHead className="text-center">STOCK</TableHead>
                <TableHead className="text-center">MÍNIMO</TableHead>
                <TableHead>PROVEEDOR</TableHead>
                <TableHead>ÁREA</TableHead>
                <TableHead>UBICACIÓN</TableHead>
                <TableHead className="text-right">COSTO PROM.</TableHead>
                <TableHead className="text-right">ACCIONES</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-10">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} className="text-center py-10 text-slate-500">
                    No hay artículos
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item) => {
                  const classInfo = getClassificationInfo(item.clasificacion);
                  const lowStock = needsRestock(item);
                  return (
                    <TableRow key={item.id} className={lowStock ? "bg-red-50" : ""}>
                      <TableCell className="font-mono font-semibold">{item.clave}</TableCell>
                      <TableCell>
                        <Badge className={`${classInfo.color} text-white`}>
                          {classInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>{item.articulo}</TableCell>
                      <TableCell className="font-medium">{item.descripcion}</TableCell>
                      <TableCell>{item.marca}</TableCell>
                      <TableCell>{item.presentacion}</TableCell>
                      <TableCell className="text-center">
                          <span className={`font-bold min-w-[40px] ${lowStock ? "text-red-600" : ""}`}>
                            {item.stock_actual}
                          </span>
                      </TableCell>
                      <TableCell className="text-center">{item.stock_minimo}</TableCell>
                      <TableCell>{item.proveedor}</TableCell>
                      <TableCell className="text-sm">{item.area_responsable}</TableCell>
                      <TableCell className="text-sm">{item.ubicacion}</TableCell>
                      <TableCell className="text-right text-sm font-semibold">
                        {(() => {
                          const artCost = articulosCostosMap[item.articulo] || 0;
                          if (artCost > 0) return `$${Number(artCost).toFixed(2)}`;
                          const costo = Number(item.costo_inicial) || 0;
                          if (costo > 0) return `$${costo.toFixed(2)}`;
                          return "—";
                        })()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canEditModule(user, "logistica_productos") && (
                            <Button size="icon" variant="ghost" onClick={() => handleEdit(item)} data-testid={`edit-item-${item.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {canDelete(user) && (
                            <Button size="icon" variant="ghost" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} data-testid={`delete-item-${item.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Editar Producto" : "Nuevo Producto"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Clasificación *</Label>
                <Select
                  value={formData.clasificacion}
                  onValueChange={(v) => setFormData({ ...formData, clasificacion: v })}
                  disabled={!!editingItem}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Artículo</Label>
                <Select
                  value={formData.articulo || "none"}
                  onValueChange={(v) => setFormData({ ...formData, articulo: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="articulo-select">
                    <SelectValue placeholder="Seleccionar artículo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin seleccionar —</SelectItem>
                    {articulosCatalogo.map(a => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción *</Label>
              <Input
                required
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Ej: Caja de clips medianos"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Marca</Label>
                <Input
                  value={formData.marca}
                  onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Presentación</Label>
                <Input
                  value={formData.presentacion}
                  onChange={(e) => setFormData({ ...formData, presentacion: e.target.value })}
                  placeholder="Ej: Caja con 100 piezas"
                />
              </div>
              <div className="space-y-2">
                <Label>Unidad</Label>
                <Select
                  value={formData.unidad}
                  onValueChange={(v) => setFormData({ ...formData, unidad: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {unidadesCatalogo.map(u => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stock Actual</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.stock_actual === 0 ? "" : formData.stock_actual}
                  onChange={(e) => setFormData({ ...formData, stock_actual: e.target.value })}
                  onFocus={(e) => { if (e.target.value === "0") e.target.value = ""; }}
                />
              </div>
              <div className="space-y-2">
                <Label>Stock Mínimo</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.stock_minimo === 0 ? "" : formData.stock_minimo}
                  onChange={(e) => setFormData({ ...formData, stock_minimo: e.target.value })}
                  onFocus={(e) => { if (e.target.value === "0") e.target.value = ""; }}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Proveedor</Label>
              <Select
                value={formData.proveedor || "none"}
                onValueChange={(v) => setFormData({ ...formData, proveedor: v === "none" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar proveedor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin proveedor</SelectItem>
                  {proveedores.map(p => (
                    <SelectItem key={p.id} value={p.nombre_comercial}>
                      {p.nombre_comercial}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Área Responsable</Label>
                <Select
                  value={formData.area_responsable || "none"}
                  onValueChange={(v) => setFormData({ ...formData, area_responsable: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="area-responsable-select">
                    <SelectValue placeholder="Seleccionar área" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {areasResponsables.map(a => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ubicación</Label>
                <Select
                  value={formData.ubicacion || "none"}
                  onValueChange={(v) => setFormData({ ...formData, ubicacion: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="ubicacion-select">
                    <SelectValue placeholder="Seleccionar ubicación" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {ubicaciones.map(u => (
                      <SelectItem key={u} value={u}>{u}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Costo Inicial (MXN)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.costo_inicial === 0 ? "" : formData.costo_inicial}
                onChange={(e) => setFormData({ ...formData, costo_inicial: e.target.value })}
                onFocus={(e) => { if (e.target.value === "0") e.target.value = ""; }}
                placeholder="0.00"
                data-testid="costo-inicial-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                {editingItem ? "Guardar Cambios" : "Crear Producto"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default Logistics;
